import { useMemo, useState } from "react";
import type { ObjectFrame } from "../api/types";
import { useAuth } from "../state/AuthContext";

export function useObjectVisibility(sessionId: string, frames: ObjectFrame[]) {
  const { user } = useAuth();
  const key = `pads.objectVisibility.v1:${user?.id ?? "local"}:${sessionId}`;
  // null includes all types, including results that arrive later; [] hides all.
  // ReviewWorkspace is keyed by session ID, keeping each recording independent.
  const [selected, setSelected] = useState<string[] | null>(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(key) ?? "null");
      if (Array.isArray(stored) && stored.every(label => typeof label === "string")) return stored;
    } catch { /* Missing or inaccessible preferences default to showing all. */ }
    return null;
  });
  const [saved, setSaved] = useState(true);
  const labels = useMemo(() => {
    const found = new Set<string>();
    for (const frame of frames) for (const object of frame.objects) found.add(object.label);
    return [...found].sort((a, b) => a.localeCompare(b));
  }, [frames]);
  const selection = useMemo(() => selected === null ? null : new Set(selected), [selected]);
  const update = (next: string[] | null) => {
    setSelected(next);
    try { localStorage.setItem(key, JSON.stringify(next)); setSaved(true); }
    catch { setSaved(false); }
  };
  return { labels, selection, update, saved };
}

export function ObjectVisibilitySettings({ labels, selection, onChange, saved, enabled }: {
  labels: string[]; selection: ReadonlySet<string> | null;
  onChange: (labels: string[] | null) => void; saved: boolean; enabled: boolean;
}) {
  const [query, setQuery] = useState("");
  const shown = labels.filter(label => selection === null || selection.has(label)).length;
  const matching = labels.filter(label => label.toLowerCase().includes(query.trim().toLowerCase()));
  return <>
    <p className="tiny muted">Choose which detected object types to show.</p>
    {labels.length ? <>
      <div className="object-filter-actions">
        <button className="ghost" disabled={selection === null} onClick={() => onChange(null)}>Show all</button>
        <button className="ghost" disabled={selection?.size === 0} onClick={() => onChange([])}>Hide all</button>
        <span className="tiny muted" role="status">{shown} of {labels.length} shown</span>
      </div>
      {(labels.length > 8 || query !== "") && <input type="search" className="object-filter-search"
        aria-label="Find object types" placeholder="Find an object type…" value={query}
        onChange={event => setQuery(event.target.value)} />}
      <div className="object-filter-list" role="group" aria-label="Visible object types">
        {matching.map(label => <label className="object-filter-option" key={label}>
          <input type="checkbox" checked={selection === null || selection.has(label)} onChange={event => {
            const next = new Set(selection ?? labels);
            if (event.target.checked) next.add(label); else next.delete(label);
            onChange([...next]);
          }} />
          <span>{label}</span>
        </label>)}
        {!matching.length && <p className="tiny muted">No matching object types.</p>}
      </div>
    </> : <p className="tiny muted">No detected object types are available yet.</p>}
    {!enabled && <p className="tiny muted">Turn on Objects to see the selected types.</p>}
    <p className="tiny muted">{saved
      ? "Saved for this recording in this browser."
      : "Browser storage is unavailable. Changes apply for this visit only."} Detection results are unchanged.</p>
  </>;
}
